import React from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import { FormattedMessage } from '@techstyle/react-intl';

import { desktop, mobile } from '../../styles';
import { BasicButton } from '../../styles/button';
import { Paragraph } from '../../styles/paragraph';
import { getProductCategoryTypes } from '../../utils/productDetail';
import { Component as FilterReviews } from '../FilterReviews';
import { Component as Pagination } from '../Pagination';
import { Component as ProductDetailReview } from '../ProductDetailReview';
import { Component as ProductDetailStars } from '../ProductDetailStars';
import { Component as SortReviews } from '../SortReviews';
import { Heading } from '../Typography';

const emptySummary = [
  { label: 'Overall', average: 0 },
  { label: 'Style', average: 0 },
  { label: 'Comfort', average: 0 },
];

const Container = styled.section`
  margin: 0 auto;
  max-width: 1024px;
  padding: 0;
  text-align: center;

  ${desktop`
    text-align: left;
    padding-top: ${props => (props.isSet ? 0 : props.theme.spacing.xlarge)}px;
    padding-right: ${props =>
      props.isSet ? props.theme.spacing.small : props.theme.spacing.medium}px;
    padding-bottom: ${props =>
      props.isSet
        ? props.theme.spacing.moderate
        : props.theme.spacing.xlarge}px;
    padding-left: ${props =>
      props.isSet ? props.theme.spacing.small : props.theme.spacing.medium}px;
  `};

  ${ProductDetailReview.Rating} {
    @media (min-width: ${({ theme }) =>
        theme.breakpoints.large}px) and (max-width: ${({ theme }) =>
        theme.breakpoints.xxlarge}px) {
      margin: 0;
    }
  }
`;

const Header = styled.header`
  text-align: left;

  ${props =>
    props.empty &&
    `
    border-bottom-color: ${props.theme.colors.subdued};
    color: ${props.theme.colors.subdued};
  `};

  ${desktop`
    clear: both;
    display: flex;
    align-items: baseline;
    flex-direction: column;
    justify-content: space-between;
  `};
`;

const HeaderCounts = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: flex-start;
  ${desktop`
    flex: 0 0 50%;
  `}
`;

const HeaderAverages = styled.div`
  flex: 0 0 50%;
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: center;

  ${mobile`
    scroll-margin-top: 60px;
  `}
  ${desktop`
    scroll-margin-top: 130px;
  `}
`;

const ReviewCount = styled(Heading).attrs({
  level: 2,
})`
  color: inherit;
  text-align: center;
  ${props => props.theme.subHeader.variants.subHeading2.textStyles}
  color: #18181E;
`;

const StyledProductDetailStars = styled(ProductDetailStars)`
  ${props => props.theme.header.variants.h1Alt.textStyles}
  gap: 20px;
`;

const AverageRating = styled.div`
  & ${StyledProductDetailStars} {
    margin-bottom: 0;
  }

  ${mobile`
    text-align: center;
  `};

  @media (min-width: ${({ theme }) =>
      theme.breakpoints.large}px) and (max-width: ${({ theme }) =>
      theme.breakpoints.xxlarge}px) {
    margin: 0;
  }
`;

const StyledParagraph = styled(Paragraph)`
  color: inherit;
  ${props => props.theme.paragraph.variants.paragraph3Uppercase.textStyles}
`;

const AverageRatingBar = styled.div`
  ${props => props.theme.paragraph.variants.paragraph4Uppercase.textStyles}
  /* The right margin is the same size as the Carousel Images in each review. */
  ${desktop`
    max-width: 312px;
    display: block;
  `};

  width: 100%;
`;

const RatingBar = styled.div`
  display: flex;
  height: 4px;
  position: relative;

  background-color: ${props => props.theme.colors.lavender200};
`;

const Bar = styled.span`
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-left: ${props => (props.status ? props.status : 0)}%;
  width: ${props => props.width}%;

  background-color: ${props => props.theme.colors.lavender600};

  &:after,
  &:before {
    content: ' ';
    position: absolute;
    top: 0;
    bottom: 0;
    width: 2px;
    background-color: ${props => props.theme.colors.white};
  }

  &:after {
    left: 20%;
  }
  &:before {
    right: 20%;
  }
`;

const Notches = styled.span`
  &:before,
  &:after {
    content: ' ';
    position: absolute;
    top: 0;
    bottom: 0;
    width: 2px;
    background-color: ${props => props.theme.colors.white};
  }
  &:before {
    left: 40%;
  }
  &:after {
    left: 60%;
  }
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${props => props.theme.spacing.tiny}px;
`;

const Footer = styled.div`
  margin-top: ${props =>
    props.isSet ? props.theme.spacing.moderate : props.theme.spacing.large}px;
  text-align: center;

  margin-bottom: 40px;
`;

const ReviewsAndRatingsWrapper = styled.div`
  background-color: ${props => props.theme.colors.lavender100};
  display: flex;
  width: 100%;
  padding: 32px 24px;
  gap: 24px;

  ${mobile`
     display:block;
     padding: 8px 16px 16px;
  `}
`;

const FilterAndSortWrapper = styled.div`
  display: flex;
  width: 100%;
  margin: 16px 0;

  ${mobile`
    flex-direction: column;
    margin: 0 0 ${props => props.theme.spacing.moderate};
    `}

  flex-direction: column;
  gap: 40px;
`;

const StyledSortReviews = styled(SortReviews)`
  ${mobile`
    justify-content: center;
    
  `}
`;

const LoadMoreReviewsButton = styled(BasicButton)``;

const getSizeFilter = tld => {
  switch (tld) {
    case '.de':
      return [
        'Zu klein',
        'Etwas zu klein',
        'Wahre Größe',
        'Etwas zu groß',
        'Zu groß',
      ];
    case '.fr':
      return [
        'Trop petit',
        'Un peu trop petit',
        'Conforme à la taille',
        'Un peu trop grand',
        'Trop grand',
      ];
    case '.es':
      return [
        'Muy pequeño/a',
        'Algo pequeño/a',
        'Tallaje correcto',
        'Algo grande',
        'Muy grande',
      ];
    default:
      return [
        'Too small',
        'A little small',
        'True to size',
        'A little large',
        'Too large',
      ];
  }
};

class ProductDetailReviews extends React.PureComponent {
  static propTypes = {
    isSet: PropTypes.bool,
    loadReviews: PropTypes.func,
    trackProductSetReviewsPagination: PropTypes.func,
    product: PropTypes.object,
    reviews: PropTypes.shape({
      counts: PropTypes.object,
      reviews: PropTypes.array,
      summary: PropTypes.array,
      fits: PropTypes.array,
    }),
    className: PropTypes.string,
    scrollToTopReviews: PropTypes.func,
    isCombinedSet: PropTypes.bool,
    reviewSortCategory: PropTypes.string,
    selectedProductSize: PropTypes.string,
    page: PropTypes.number,
    tld: PropTypes.string,
    trackFilterReviews: PropTypes.func,
  };

  static Container = Container;
  static ReviewCount = ReviewCount;

  state = {
    isLoading: false,
    reviewSortCategory: this.props.reviewSortCategory,
    selectedProductSize: this.props.selectedProductSize,
    selectedSizeField: null,
  };

  handleLoadReviews = ({
    page,
    selectedProductSize,
    reviewSortCategory,
    isSet = false,
    combineLinkedProduct = true,
  }) => {
    const { product, loadReviews } = this.props;
    const groupCode = product.group_code;

    if (loadReviews) {
      this.setState(
        {
          isLoading: true,
          reviewSortCategory,
          selectedProductSize,
        },
        async () => {
          let result;
          let error;

          try {
            result = await this.props.loadReviews({
              masterProductId: product.master_product_id,
              count: 5,
              page: page || this.props.reviews.page + 1,
              groupCode,
              selectedProductSize,
              reviewSortCategory,
              isSet,
              combineLinkedProduct,
            });
          } catch (err) {
            error = err;
          }

          if (!result || error) {
            // if something went wrong, do nothing and hide the button
            return this.setState({
              error,
              isLoading: false,
            });
          }

          if (isSet) {
            this.props.scrollToTopReviews &&
              this.props.scrollToTopReviews(product.master_product_id);
          }

          return this.setState({
            isLoading: false,
          });
        }
      );
    }
  };

  handleLoadMore = (page = null, isSet = false) => {
    const { isCombinedSet } = this.props;
    const { reviewSortCategory, selectedProductSize } = this.state;
    if (page) {
      this.handleLoadReviews({
        page: page,
        selectedProductSize,
        reviewSortCategory,
        isSet,
        combineLinkedProduct: isSet && isCombinedSet,
      });
    } else {
      this.handleLoadReviews({
        selectedProductSize,
        reviewSortCategory,
      });
    }
  };

  handleFilterChange = sizeInfo => {
    const { reviewSortCategory, selectedSizeField: currentSizeField } =
      this.state;
    const selectedProductSize = sizeInfo?.newProductSize || null;
    const selectedSizeField = sizeInfo?.sizeField;
    const filter = {
      type: sizeInfo ? selectedSizeField : currentSizeField,
      value: sizeInfo ? selectedProductSize : 'clear',
    };

    this.props.trackFilterReviews(reviewSortCategory, [filter]);

    this.setState({ selectedSizeField });

    this.handleLoadReviews({
      page: 1,
      selectedProductSize,
      reviewSortCategory,
    });
  };

  handleSortChange = reviewSortCategory => {
    const { selectedProductSize, selectedSizeField } = this.state;

    this.props.trackFilterReviews(
      reviewSortCategory,
      selectedSizeField && selectedProductSize
        ? [{ type: selectedSizeField, value: selectedProductSize }]
        : []
    );

    this.setState({ selectedSizeField });

    this.handleLoadReviews({
      page: 1,
      selectedProductSize,
      reviewSortCategory,
    });
  };

  renderReviews() {
    const { reviews, isSet } = this.props;
    return reviews.reviews.map((review, index) => {
      return (
        <ProductDetailReview
          key={`review-${index}`}
          review={review}
          isSet={isSet}
        />
      );
    });
  }

  renderAverages(summary) {
    const { reviews, tld } = this.props;
    if (!summary || !summary.length || !reviews) {
      return;
    }

    const sizeFilter = getSizeFilter(tld);

    const maxRating = Math.max.apply(
      Math,
      reviews.fits.map(function (o) {
        return o.rating;
      })
    );

    // leave this here for now

    let size =
      maxRating > 0
        ? reviews.fits.filter(field => field.rating === maxRating)
        : null;
    if (size && size.length > 1) {
      size = [{ label: 'True to size' }];
    }
    const style = summary.find(field => field.label === 'Style');
    const comfort = summary.find(field => field.label === 'Comfort');

    return (
      <HeaderAverages id="average-headers">
        <AverageRatingBar data-autotag="desktop-style-bar">
          <Row>
            <FormattedMessage
              id="site_pdp.review_small_size_label"
              defaultMessage="Small"
            />
            <FormattedMessage
              id="site_pdp.review_true_to_size_label"
              defaultMessage="True to size"
            />
            <FormattedMessage
              id="site_pdp.review_large_size_label"
              defaultMessage="Large"
            />
          </Row>
          <RatingBar>
            <Bar
              data-testid="size-review-bar"
              status={
                size && size.length === 1 && size[0].label
                  ? sizeFilter.indexOf(size[0].label) * 20
                  : 0
              }
              width={size ? 20 : 0}
            />
            <Notches />
          </RatingBar>
        </AverageRatingBar>
        <AverageRatingBar data-autotag="desktop-style-bar">
          <Row>
            <FormattedMessage
              id="site_pdp.review_style"
              defaultMessage="Style"
            />
            <span>{style.average.toFixed(1)}</span>
          </Row>
          <RatingBar>
            <Bar width={100 * (style.average / 5)} />
            <Notches />
          </RatingBar>
        </AverageRatingBar>
        <AverageRatingBar data-autotag="desktop-comfort-bar">
          <Row>
            <FormattedMessage
              id="site_pdp.review_comfort"
              defaultMessage="Comfort"
            />
            <span>{comfort.average.toFixed(1)}</span>
          </Row>
          <RatingBar>
            <Bar width={100 * (comfort.average / 5)} />
            <Notches />
          </RatingBar>
        </AverageRatingBar>
      </HeaderAverages>
    );
  }

  renderCounts() {
    const reviews = this.props.reviews;
    const { counts } = reviews;
    if (!counts) {
      return;
    }

    const recommend = counts.recommendedPercentage.toFixed(0);
    const reviewCount = counts.reviewCount;

    const reviewSummary =
      reviewCount > 0 ? (
        <FormattedMessage
          id="site_pdp.review_recommend_this_product"
          defaultMessage="{recommend}% of reviewers would recommend this product"
          values={{ recommend: recommend }}
        />
      ) : (
        <FormattedMessage
          id="site_pdp.zero_reviews_recommend_this_product"
          defaultMessage="There are no reviews on this product"
        />
      );

    return (
      <HeaderCounts>
        <AverageRating>
          <StyledProductDetailStars
            reviews={reviews}
            showAverageRating
            starSize={24}
          />
        </AverageRating>
        <ReviewCount data-autotag="pdpNumberReviews">
          <FormattedMessage
            id="site_pdp.review_count_label"
            defaultMessage="{count} {count, plural, one {Review} other {Reviews}}"
            values={{ count: reviewCount }}
          />
        </ReviewCount>
        <StyledParagraph size={14} data-autotag="analysed-review">
          {reviewSummary}
        </StyledParagraph>
      </HeaderCounts>
    );
  }

  renderReviewFilters = () => {
    const { product, reviews } = this.props;
    const { selectedProductSize } = this.state;
    const {
      product_category_id_list: productCategoryIdList,
      tag_id_list: tagIdList,
    } = product;

    const { isBra, isBralette, isLingerie, isMen, isSleep, isUndies } =
      getProductCategoryTypes(productCategoryIdList, tagIdList);

    return (
      <FilterReviews
        sizes={reviews.sizes}
        isBra={isBra}
        isBralette={isBralette}
        isLingerie={isLingerie}
        isMen={isMen}
        isSleep={isSleep}
        isUndies={isUndies}
        onChange={this.handleFilterChange}
        product={product}
        selectedProductSize={selectedProductSize}
      />
    );
  };

  renderSortReviews = () => {
    const { reviewSortCategory } = this.state;
    return (
      <StyledSortReviews
        onChange={this.handleSortChange}
        reviewSortCategory={reviewSortCategory}
      />
    );
  };

  onSetReviewsPageChanged = data => {
    const { currentPage } = data;
    this.handleLoadMore(currentPage, true);
    this.props.trackProductSetReviewsPagination(currentPage);
  };

  renderPagination = () => (
    <Pagination
      totalRecords={this.props.reviews.counts.recommendedCount}
      pageLimit={5}
      onPageChanged={this.onSetReviewsPageChanged}
    />
  );

  render() {
    const { reviews, className, isSet } = this.props;

    if (!reviews) {
      /* Reviews are unavailable */
      return (
        <Container className={className} isSet={isSet}>
          <Header empty>
            <ReviewsAndRatingsWrapper>
              <HeaderCounts>
                <ReviewCount>
                  <FormattedMessage
                    id="site_pdp.review_count_label_unavailable"
                    defaultMessage="Reviews are currently unavailable."
                  />
                </ReviewCount>
                <AverageRating>
                  <StyledProductDetailStars showAverageRating />
                </AverageRating>
              </HeaderCounts>
              {this.renderAverages(emptySummary)}
            </ReviewsAndRatingsWrapper>
          </Header>
        </Container>
      );
    }

    if (!reviews.reviews || !reviews.reviews.length) {
      /* 0 reviews but there may be ratings */
      return (
        <Container className={className} isSet={isSet}>
          <Header>
            <ReviewsAndRatingsWrapper>
              {this.renderCounts()}
              {this.renderAverages(reviews.summary || emptySummary)}
            </ReviewsAndRatingsWrapper>

            <FilterAndSortWrapper>
              {this.renderSortReviews()}
              {this.renderReviewFilters()}
            </FilterAndSortWrapper>
          </Header>
        </Container>
      );
    }

    const showLoadMore =
      reviews.reviews.length !== reviews.counts.reviewCount &&
      !this.state.error;

    return (
      <Container className={className} isSet={isSet}>
        {!isSet && (
          <Header>
            <ReviewsAndRatingsWrapper>
              {this.renderCounts()}
              {this.renderAverages(reviews.summary)}
            </ReviewsAndRatingsWrapper>

            <FilterAndSortWrapper>
              {this.renderSortReviews()}
              {this.renderReviewFilters()}
            </FilterAndSortWrapper>
          </Header>
        )}

        {this.renderReviews()}
        <Footer isSet={isSet}>
          {showLoadMore && !isSet && (
            <LoadMoreReviewsButton
              disabled={this.state.isLoading}
              onClick={() => this.handleLoadMore(null, false)}
              data-autotag="load-reviews"
            >
              <FormattedMessage
                id="site_pdp.review_load_more"
                defaultMessage="Load More"
              />
            </LoadMoreReviewsButton>
          )}
          {isSet && this.renderPagination()}
        </Footer>
      </Container>
    );
  }
}

export default ProductDetailReviews;
